import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { I } from '../icons-ds';
import { Card, Row, SectionHeader } from '../ui';
import { MobileNav, useViewport } from '../layout';
import { usePhrases } from '../lib/PhrasesContext';

export function FoldersScreen() {
  const viewport = useViewport();
  const navigate = useNavigate();
  const isMobile = viewport === 'mobile';

  const { folders, phrases } = usePhrases();

  const countByKind = useMemo(() => {
    const m: Record<string, number> = {};
    for (const p of phrases) m[p.primaryTag] = (m[p.primaryTag] || 0) + 1;
    return m;
  }, [phrases]);

  const subfolderTotal = folders.reduce((s, f) => s + (f.children?.length || 0), 0);

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-grouped)' }}>
      {isMobile && (
        <MobileNav
          title="Folders"
          onBack={() => navigate(-1)}
          backTitle="Back"
        />
      )}

      <div style={{ padding: isMobile ? '0 16px 32px' : '24px 32px 48px', maxWidth: 980, margin: '0 auto' }}>
        {!isMobile && (
          <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div>
              <div className="t-largetitle" style={{ marginBottom: 4 }}>Folders</div>
              <div className="t-subhead" style={{ color: 'var(--fg-secondary)' }}>
                {folders.length} categories · {subfolderTotal} subfolders · organized by clinical context
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {folders.map((parent) => {
            const children = parent.children ?? [];
            const total = countByKind[parent.kind] ?? children.reduce((s, c) => s + c.count, 0);
            return (
              <div key={parent.id}>
                <SectionHeader>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 3, background: parent.color }} />
                    {parent.name}
                    <span style={{ color: 'var(--fg-quaternary)', fontWeight: 500 }}>· {total} phrases</span>
                  </span>
                </SectionHeader>
                <Card padded={false}>
                  {children.length > 0 ? (
                    children.map((child, i) => (
                      <Row
                        key={child.id}
                        leading={
                          <span
                            style={{
                              width: 28, height: 28, borderRadius: 8,
                              background: `color-mix(in oklab, ${parent.color} 16%, transparent)`,
                              color: parent.color,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >
                            <I.folder size={16} />
                          </span>
                        }
                        title={child.name}
                        subtitle={`${child.count} ${child.count === 1 ? 'phrase' : 'phrases'}`}
                        trailing={<I.chevR size={16} />}
                        onClick={() => navigate(`/folder/${parent.id}/${child.id}`)}
                        last={i === children.length - 1}
                      />
                    ))
                  ) : (
                    <Row
                      leading={
                        <span
                          style={{
                            width: 28, height: 28, borderRadius: 8,
                            background: `color-mix(in oklab, ${parent.color} 16%, transparent)`,
                            color: parent.color,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <I.folder size={16} />
                        </span>
                      }
                      title={`All ${parent.name.toLowerCase()} phrases`}
                      subtitle={`${total} ${total === 1 ? 'phrase' : 'phrases'}`}
                      trailing={<I.chevR size={16} />}
                      onClick={() => navigate(`/folder/${parent.id}`)}
                      last
                    />
                  )}
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default FoldersScreen;
